// src/hooks/useAIInsights.js
// ══════════════════════════════════════════════════════════════════════════════════════
// Enterprise React Hook: useAIInsights
// ══════════════════════════════════════════════════════════════════════════════════════
// Architecture: 
//   - 100% Server-Side Compute Bias: Prioritizes fetching pre-calculated O(1) Redis 
//     insights from the backend to ensure zero client-side UI thread blocking.
//   - Ultra-Lightweight Digital Twin Context: Cross-references live Redux state 
//     for immediate operational fallbacks (SLA breaches, workload imbalance).
//   - Real-Time WebSocket Anomalies: Injects critical physical twin anomalies directly
//     into the top of the insights stack with zero-latency.
//   - Advanced Debugging: In-memory log retention with CSV/JSON export capabilities.
// --- UPDATE (v2.1 Asset AI connection)
//   - insightsByAsset / getInsightsForAsset(): every insight that references an asset
//     (asset_id, assetId, actionPayload.assetId) is indexed per asset, so tables, the
//     Asset Details modal and the AI Asset Intelligence panel can show them per asset.
//   - Live 'asset_prediction_update' (failure-model output) and HIGH/CRITICAL
//     'ai_flood_prediction' (hydraulic model) events are injected as asset-linked insights.
//   - WebSocket handlers read snoozed ids / generateInsights through refs and subscribe
//     ONCE (was: resubscribed on every work-order change or snooze, which could drop
//     events arriving between unsubscribe and subscribe).
// --- END UPDATE
// ══════════════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'; // --- UPDATE: + useMemo
import { 
  MdWarning, 
  MdPriorityHigh, 
  MdTrendingUp, 
  MdTimeline, 
  MdAutoAwesome, 
  MdPeople, 
  MdTimerOff, 
  MdSensors, 
  MdPrecisionManufacturing,
  MdOutlineReportProblem,
  MdSpeed,
  MdWaterDrop, // --- UPDATE
} from 'react-icons/md';
import aiInsightsService from '../services/aiInsightsService';
import webSocketService from '../services/websocketService';
// --- UPDATE
import { groupInsightsByAsset } from '../components/public_utilities/assetAiGuide';
// --- END UPDATE

// ============================================================================
// CONSTANTS & THRESHOLDS
// ============================================================================
const INSIGHT_SEVERITY = {
  CRITICAL: 'critical',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success'
};

const SEVERITY_WEIGHT = {
  [INSIGHT_SEVERITY.CRITICAL]: 4,
  [INSIGHT_SEVERITY.WARNING]: 3,
  [INSIGHT_SEVERITY.INFO]: 2,
  [INSIGHT_SEVERITY.SUCCESS]: 1
};

const THRESHOLDS = {
  OVERDUE_CRITICAL: 10,
  OVERDUE_WARNING: 1,
  BACKLOG_RATIO: 1.2, // Active vs Completed
  STAGNANT_MS: 3 * 24 * 60 * 60 * 1000, // 3 days
  WORKLOAD_MULTIPLIER: 1.5,
  MIN_TICKETS_FOR_BALANCE: 2
};

/**
 * Custom hook for AI-powered insights and anomaly detection.
 * * @param {Array} workOrders - Live array of work order objects from Redux/Context
 * @param {Object} stats - High-level work order statistics
 * @param {Object} options - Configuration options
 * @returns {Object} - Extensive insights state, computed properties, and utility controls
 */
export const useAIInsights = (workOrders = [], stats = {}, options = {}) => {
  const {
    enabled = true,
    autoRefresh = true,      
    refreshInterval = 300000, // 5 minutes standard for O(1) polling
    debugMode = false,
    maxLogHistory = 200,
  } = options;

  // ────────────────────────────────────────────────────────────────────────────
  // STATE
  // ────────────────────────────────────────────────────────────────────────────
  const [insights, setInsights] = useState([]);
  const [snoozedIds, setSnoozedIds] = useState(new Set()); // Session-based snooze
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [generationCount, setGenerationCount] = useState(0);
  const [wsStatus, setWsStatus] = useState('disconnected');

  // ────────────────────────────────────────────────────────────────────────────
  // REFS
  // ────────────────────────────────────────────────────────────────────────────
  const refreshTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const previousWorkOrdersRef = useRef([]);
  const previousStatsRef = useRef({});
  const debugLogsRef = useRef([]);
  // --- UPDATE: latest values for the subscribe-once WebSocket effect
  const snoozedRef = useRef(snoozedIds);
  snoozedRef.current = snoozedIds;
  const generateRef = useRef(null);
  // --- END UPDATE

  // ────────────────────────────────────────────────────────────────────────────
  // ADVANCED DEBUG LOGGER
  // ────────────────────────────────────────────────────────────────────────────
  const log = useCallback((level, message, data = null) => {
    if (!debugMode) return;
    
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: level.toUpperCase(),
      message,
      data: data ? JSON.stringify(data) : null
    };

    // Keep in-memory history for debugging exports
    debugLogsRef.current = [logEntry, ...debugLogsRef.current].slice(0, maxLogHistory);

    const consoleMethod = console[level] || console.log;
    consoleMethod(`[${timestamp}] [useAIInsights] [${level.toUpperCase()}] ${message}`, data || '');
  }, [debugMode, maxLogHistory]);

  // ────────────────────────────────────────────────────────────────────────────
  // STATE CHANGE DETECTION (Deep Optimization)
  // ────────────────────────────────────────────────────────────────────────────
  const hasDataChanged = useCallback(() => {
    // Prevent deep equality checks on massive arrays if lengths differ
    if (workOrders.length !== previousWorkOrdersRef.current.length) return true;
    
    const workOrdersChanged = JSON.stringify(workOrders) !== JSON.stringify(previousWorkOrdersRef.current);
    const statsChanged = JSON.stringify(stats) !== JSON.stringify(previousStatsRef.current);
    
    return workOrdersChanged || statsChanged;
  }, [workOrders, stats]);

  // ────────────────────────────────────────────────────────────────────────────
  // LOCAL HEURISTIC GENERATORS (Ultra-Lightweight Context)
  // ────────────────────────────────────────────────────────────────────────────
  
  const computeOverdueInsights = useCallback((currentStats, now) => {
    const results = [];
    if (!currentStats || currentStats.overdue <= 0) return results;

    const isCritical = currentStats.overdue >= THRESHOLDS.OVERDUE_CRITICAL;
    
    results.push({
      id: 'local-overdue',
      type: isCritical ? INSIGHT_SEVERITY.CRITICAL : INSIGHT_SEVERITY.WARNING,
      severity: isCritical ? INSIGHT_SEVERITY.CRITICAL : INSIGHT_SEVERITY.WARNING,
      icon: MdWarning,
      title: isCritical ? 'Critical SLA Breach Warning' : 'High Overdue Backlog',
      message: `${currentStats.overdue} work orders are overdue. Consider reassigning tasks to improve SLA response time immediately.`,
      action: 'View Overdue',
      actionType: 'filter',
      actionPayload: { filter: 'overdue' },
      timestamp: now.toISOString()
    });

    return results;
  }, []);

  const computeBacklogInsights = useCallback((currentStats, now) => {
    const results = [];
    if (!currentStats) return results;

    const activeCount = (currentStats.pending || 0) + (currentStats.in_progress || 0);
    const completedCount = currentStats.completed || 0;

    if (activeCount > (completedCount * THRESHOLDS.BACKLOG_RATIO) && activeCount > 5) {
      results.push({
        id: 'local-backlog-buildup',
        type: INSIGHT_SEVERITY.INFO,
        severity: INSIGHT_SEVERITY.INFO,
        icon: MdTimeline, 
        title: 'System Backlog Building Up',
        message: `Active work orders (${activeCount}) significantly exceed completed ones (${completedCount}). Monitor resource allocation closely.`,
        action: 'View Backlog',
        actionType: 'view-backlog',
        actionPayload: { filter: 'pending' },
        timestamp: now.toISOString()
      });
    }
    return results;
  }, []);

  const computeWorkloadInsights = useCallback((currentWOs, now) => {
    const results = [];
    if (!currentWOs || currentWOs.length === 0) return results;

    const userLoad = {};
    let assignedCount = 0;
    
    currentWOs.forEach(wo => {
        if (wo.assigned_user_id && wo.status === 'in_progress') {
            userLoad[wo.assigned_user_id] = (userLoad[wo.assigned_user_id] || 0) + 1;
            assignedCount++;
        }
    });

    const activeTechs = Object.keys(userLoad).length;
    if (activeTechs > 1) {
        const avgLoad = assignedCount / activeTechs;
        const overloadedTechs = Object.entries(userLoad).filter(
          ([id, count]) => count > (avgLoad * THRESHOLDS.WORKLOAD_MULTIPLIER) && count >= THRESHOLDS.MIN_TICKETS_FOR_BALANCE
        );
        
        if (overloadedTechs.length > 0) {
            results.push({
                id: 'local-workload-imbalance',
                type: INSIGHT_SEVERITY.WARNING,
                severity: INSIGHT_SEVERITY.WARNING,
                icon: MdPeople,
                title: 'Workload Imbalance Detected',
                message: `${overloadedTechs.length} technician(s) have a significantly higher active workload than the fleet average. Risk of burnout or task delays.`,
                action: 'Balance Workload',
                actionType: 'balance',
                actionPayload: null,
                timestamp: now.toISOString()
            });
        }
    }
    return results;
  }, []);

  const computeStagnantInsights = useCallback((currentWOs, now) => {
    const results = [];
    if (!currentWOs) return results;

    const stagnantTickets = currentWOs.filter(wo => {
        if (wo.status !== 'in_progress' || !wo.updated_at) return false;
        const lastUpdate = new Date(wo.updated_at);
        return (now.getTime() - lastUpdate.getTime()) > THRESHOLDS.STAGNANT_MS;
    });

    if (stagnantTickets.length > 0) {
        results.push({
            id: 'local-stagnant-tickets',
            type: INSIGHT_SEVERITY.INFO,
            severity: INSIGHT_SEVERITY.INFO,
            icon: MdTimerOff,
            title: 'Stalled Operational Progress',
            message: `${stagnantTickets.length} tickets have been "In Progress" for over 72 hours without updates. Verify technician status.`,
            action: 'View Stalled',
            actionType: 'optimize',
            actionPayload: { filter: 'stagnant' },
            timestamp: now.toISOString()
        });
    }
    return results;
  }, []);

  const computeAITaskInsights = useCallback((currentWOs, now) => {
    const results = [];
    if (!currentWOs) return results;

    // Predictive Maintenance Needs
    const predictiveTasks = currentWOs.filter(wo => 
      wo.source === 'ai_prediction' && !wo.assigned_user_id && wo.status !== 'completed'
    ).length;

    if (predictiveTasks > 0) {
      results.push({
        id: 'local-predictive-dispatch',
        type: INSIGHT_SEVERITY.WARNING, 
        severity: INSIGHT_SEVERITY.WARNING,
        icon: MdPrecisionManufacturing,
        title: 'Predictive Maintenance Required',
        message: `${predictiveTasks} AI-generated preventative maintenance tasks await assignment. Address these to prevent imminent physical asset failure.`,
        action: 'Auto-Assign',
        actionType: 'bulk-assign',
        actionPayload: { filter: 'ai_prediction' },
        timestamp: now.toISOString()
      });
    }

    // Unassigned AI Routing Suggestions
    const routingSuggestions = currentWOs.filter(wo => 
      !wo.assigned_user_id && wo.suggested_user_id && wo.status !== 'completed' && wo.status !== 'cancelled'
    ).length;

    if (routingSuggestions > 0) {
      results.push({
        id: 'local-ai-routing-suggestions',
        type: INSIGHT_SEVERITY.INFO, 
        severity: INSIGHT_SEVERITY.INFO,
        icon: MdAutoAwesome,
        title: 'AI Assignment Routing Available',
        message: `${routingSuggestions} unassigned orders have high-confidence AI routing suggestions waiting for dispatcher approval.`,
        action: 'Auto-Assign All',
        actionType: 'bulk-assign',
        actionPayload: { count: routingSuggestions },
        timestamp: now.toISOString()
      });
    }

    return results;
  }, []);

  // ────────────────────────────────────────────────────────────────────────────
  // MAIN GENERATOR (O(1) Redis + Local Context)
  // ────────────────────────────────────────────────────────────────────────────
  const generateInsights = useCallback(async (force = false) => {
    if (!enabled && !force) {
      log('debug', 'Insights generation disabled via options');
      return;
    }

    // Bypass execution if data is identical, to save render cycles
    if (!force && !hasDataChanged() && generationCount > 0) {
      log('debug', 'State unchanged; bypassing generation cycle');
      return;
    }

    const startTime = performance.now();
    log('info', 'Executing core insights generation cycle', { force });

    setIsGenerating(true);
    setError(null);

    try {
      let mergedInsights = [];

      // 1. Fetch pre-calculated O(1) Redis insights from the backend
      // This shifts 90% of the heavy compute off the client UI thread.
      try {
        log('info', 'Requesting O(1) Redis Insights from ML Engine');
        const backendResponse = await aiInsightsService.getInsights();
        if (backendResponse?.success && backendResponse?.data) {
          mergedInsights = [...backendResponse.data];
          log('info', `Retrieved ${mergedInsights.length} insights from backend`);
        }
      } catch (backendErr) {
        log('warn', 'Backend ML insights fetch failed. Falling back to local twin context', backendErr);
      }

      // 2. Add ultra-lightweight Digital Twin context based on live Redux state
      const now = new Date();
      const localInsights = [
        ...computeOverdueInsights(stats, now),
        ...computeBacklogInsights(stats, now),
        ...computeWorkloadInsights(workOrders, now),
        ...computeStagnantInsights(workOrders, now),
        ...computeAITaskInsights(workOrders, now)
      ];

      // Merge backend and local insights, deduplicating by ID
      const finalInsightsMap = new Map();
      
      // We reverse the array to ensure local overrides backend if IDs match (optional architecture choice)
      [...mergedInsights, ...localInsights].forEach(insight => {
        // Skip insights the user has explicitly snoozed this session
        if (snoozedIds.has(insight.id)) return;
        
        if (!finalInsightsMap.has(insight.id)) {
          finalInsightsMap.set(insight.id, insight);
        }
      });
      
      const combinedInsights = Array.from(finalInsightsMap.values());

      if (mountedRef.current) {
        // Sort insights: Critical > Warning > Info > Success
        combinedInsights.sort((a, b) => {
          const weightA = SEVERITY_WEIGHT[a.severity] ?? SEVERITY_WEIGHT[a.type] ?? 0;
          const weightB = SEVERITY_WEIGHT[b.severity] ?? SEVERITY_WEIGHT[b.type] ?? 0;
          
          // Secondary sort by timestamp if weights are identical
          if (weightA === weightB && a.timestamp && b.timestamp) {
             return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          }
          return weightB - weightA;
        });

        setInsights(combinedInsights);
        setLastGenerated(new Date());
        setGenerationCount(prev => prev + 1);

        // Update refs for subsequent change detection
        previousWorkOrdersRef.current = workOrders;
        previousStatsRef.current = stats;

        log('info', 'Insights cycle completed successfully', {
          totalInsights: combinedInsights.length,
          executionTimeMs: (performance.now() - startTime).toFixed(2),
        });
      }
    } catch (err) {
      log('error', 'Fatal error during insights generation', err);
      if (mountedRef.current) setError(err.message || 'Failed to generate insights');
    } finally {
      if (mountedRef.current) setIsGenerating(false);
    }
  }, [
    enabled, workOrders, stats, hasDataChanged, generationCount, snoozedIds, 
    log, computeOverdueInsights, computeBacklogInsights, computeWorkloadInsights, 
    computeStagnantInsights, computeAITaskInsights
  ]);

  // ────────────────────────────────────────────────────────────────────────────
  // REAL-TIME DIGITAL TWIN WEBSOCKET INJECTION
  // --- UPDATE: subscribe once; asset-linked insights from live ML events
  // ────────────────────────────────────────────────────────────────────────────
  generateRef.current = generateInsights;

  useEffect(() => {
    if (!enabled) return;

    // Push an insight to the top of the stack unless snoozed / duplicate
    const inject = (insight) => {
        setInsights(prev => {
            if (snoozedRef.current.has(insight.id)) return prev;
            if (prev.find(i => i.id === insight.id)) return prev;
            return [insight, ...prev];
        });
    };

    // Handler 1: Physical Twin Critical Anomalies
    const handleCriticalAnomaly = (data = {}) => {
        log('warn', 'Real-time WebSocket critical anomaly injected', data);
        inject({
            id: `ws-anomaly-${data.sensor_id}-${data.timestamp || Date.now()}`,
            type: INSIGHT_SEVERITY.CRITICAL,
            severity: INSIGHT_SEVERITY.CRITICAL,
            icon: MdSensors,
            title: 'CRITICAL: Live Twin Anomaly Detected',
            message: `Sensor ${data.sensor_id} reported a severe parameter deviation (Z-Score: ${data.z_score || 'Unknown'}). Imminent asset damage probability is high.`,
            action: 'Launch Digital Twin',
            actionType: 'navigate',
            actionPayload: { route: '/admin/digital-twin', assetId: data.asset_id },
            asset_id: data.asset_id ?? null, // --- UPDATE
            timestamp: new Date().toISOString(),
            isLiveInjection: true // Flag for UI animation
        });
    };

    // Handler 2: ML Model Retraining Complete
    const handleModelRetrained = (data) => {
        log('info', 'WebSocket notification: ML Model Retrained. Forcing silent refresh.', data);
        generateRef.current?.(true);
    };

    // Handler 3 (--- UPDATE): failure-model prediction for one asset
    const handleAssetPrediction = (data = {}) => {
        const assetId = data.assetId ?? data.asset_id;
        if (assetId == null) return;
        const conf = Number(data.confidence);
        const confPct = Number.isFinite(conf) ? Math.round(conf <= 1 ? conf * 100 : conf) : null;
        log('warn', 'Live asset prediction injected', data);
        inject({
            id: `ws-pred-${assetId}-${data.timestamp || Date.now()}`,
            type: confPct != null && confPct >= 80 ? INSIGHT_SEVERITY.CRITICAL : INSIGHT_SEVERITY.WARNING,
            severity: confPct != null && confPct >= 80 ? INSIGHT_SEVERITY.CRITICAL : INSIGHT_SEVERITY.WARNING,
            icon: MdPrecisionManufacturing,
            title: `Predicted degradation: ${data.assetName || `Asset ${assetId}`}`,
            message: `The failure model recommends: ${data.action || 'inspection'}${confPct != null ? ` (confidence ${confPct}%)` : ''}.`,
            action: 'Open Asset',
            actionType: 'open-asset',
            actionPayload: { assetId },
            asset_id: assetId,
            confidence: confPct,
            model: 'failure',
            timestamp: new Date().toISOString(),
            isLiveInjection: true,
        });
    };

    // Handler 4 (--- UPDATE): hydraulic flood model — only HIGH / CRITICAL are surfaced
    const handleFloodPrediction = (data = {}) => {
        const assetId = data.well_id ?? data.asset_id;
        const severity = String(data.predictedSeverity || data.flood_risk || '').toUpperCase();
        if (assetId == null || (severity !== 'HIGH' && severity !== 'CRITICAL')) return;
        const prob = Number(data.floodRiskProbability ?? data.flood_probability);
        log('warn', 'Live flood prediction injected', data);
        inject({
            id: `ws-flood-${assetId}-${data.timestamp || Date.now()}`,
            type: severity === 'CRITICAL' ? INSIGHT_SEVERITY.CRITICAL : INSIGHT_SEVERITY.WARNING,
            severity: severity === 'CRITICAL' ? INSIGHT_SEVERITY.CRITICAL : INSIGHT_SEVERITY.WARNING,
            icon: MdWaterDrop,
            title: `Flood risk ${severity}: ${data.wellName || `Well ${assetId}`}`,
            message: `The hydraulic model estimates ${Number.isFinite(prob) ? `${Math.round(prob * 100)}%` : 'high'} flood / surcharge risk. Check drainage and pre-stage pumping.`,
            action: 'Open Asset',
            actionType: 'open-asset',
            actionPayload: { assetId },
            asset_id: assetId,
            model: 'hydraulic',
            timestamp: new Date().toISOString(),
            isLiveInjection: true,
        });
    };

    try {
        setWsStatus('connected');
        webSocketService.subscribe('critical_anomaly_detected', handleCriticalAnomaly);
        webSocketService.subscribe('model_retraining_completed', handleModelRetrained);
        webSocketService.subscribe('asset_prediction_update', handleAssetPrediction);
        webSocketService.subscribe('ai_flood_prediction', handleFloodPrediction);
    } catch (e) {
        log('error', 'Failed to subscribe to Digital Twin WS feeds', e);
        setWsStatus('error');
    }

    return () => {
        try {
            webSocketService.unsubscribe('critical_anomaly_detected', handleCriticalAnomaly);
            webSocketService.unsubscribe('model_retraining_completed', handleModelRetrained);
            webSocketService.unsubscribe('asset_prediction_update', handleAssetPrediction);
            webSocketService.unsubscribe('ai_flood_prediction', handleFloodPrediction);
            setWsStatus('disconnected');
        } catch (e) {}
    };
  }, [enabled, log]);
  // --- END UPDATE

  // ────────────────────────────────────────────────────────────────────────────
  // ACTIONS & UTILITIES
  // ────────────────────────────────────────────────────────────────────────────
  
  /**
   * Rates and permanently dismisses an insight, sending feedback to the ML engine.
   */
  const rateInsight = useCallback(async (insightId, rating) => {
    try {
      log('info', `Attempting to rate insight ${insightId} with rating ${rating}`);
      // Optimistically remove from UI
      setInsights(prev => prev.filter(i => i.id !== insightId));
      
      // Async send to backend
      await aiInsightsService.rateInsight(insightId, rating);
      log('info', 'Insight rated and dismissed successfully', { insightId });
    } catch (err) {
      log('error', 'Network error while rating insight', err);
      throw err;
    }
  }, [log]);

  /**
   * Temporarily dismisses an insight for the duration of the user session.
   */
  const snoozeInsight = useCallback((insightId) => {
    log('info', `Snoozing insight ${insightId}`);
    setSnoozedIds(prev => {
        const newSet = new Set(prev);
        newSet.add(insightId);
        return newSet;
    });
    setInsights(prev => prev.filter(i => i.id !== insightId));
  }, [log]);

  const refresh = useCallback(() => {
    log('info', 'Manual refresh requested by user');
    generateInsights(true);
  }, [generateInsights, log]);

  const clear = useCallback(() => {
    log('info', 'Clearing all active insights from state');
    setInsights([]);
    setError(null);
    setLastGenerated(null);
  }, [log]);

  const getInsightByType = useCallback((type) => {
    return insights.find(i => i.type === type);
  }, [insights]);

  const getInsightsBySeverity = useCallback((severity) => {
    return insights.filter(i => i.severity === severity || i.type === severity);
  }, [insights]);

  const filterInsights = useCallback((predicate) => {
    return insights.filter(predicate);
  }, [insights]);

  // --- Export Utilities ---
  
  const exportAsJSON = useCallback(() => {
    log('info', 'Exporting insights as JSON payload');
    const exported = JSON.stringify(insights, null, 2);
    const blob = new Blob([exported], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flowsense-insights-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [insights, log]);

  const exportAsCSV = useCallback(() => {
    log('info', 'Exporting insights as CSV payload');
    if (insights.length === 0) return;

    const headers = ['ID', 'Severity', 'Title', 'Message', 'Timestamp', 'Action'];
    const rows = insights.map(i => [
        i.id,
        i.severity || i.type,
        `"${(i.title || '').replace(/"/g, '""')}"`,
        `"${(i.message || '').replace(/"/g, '""')}"`,
        i.timestamp || new Date().toISOString(),
        i.action || 'None'
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flowsense-insights-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [insights, log]);

  const downloadDebugLogs = useCallback(() => {
    if (!debugMode) {
      console.warn("Debug mode is disabled. No logs collected.");
      return;
    }
    const blob = new Blob([JSON.stringify(debugLogsRef.current, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flowsense-ai-debug-logs-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [debugMode]);

  // --- UPDATE: per-asset index
  const insightsByAsset = useMemo(() => groupInsightsByAsset(insights), [insights]);
  const getInsightsForAsset = useCallback(
    (assetId) => (assetId == null ? [] : insightsByAsset.get(String(assetId)) || []),
    [insightsByAsset]
  );
  // --- END UPDATE

  const getCacheStats = useCallback(() => {
    return aiInsightsService.getCacheStats ? aiInsightsService.getCacheStats() : { size: 0, status: 'unavailable' };
  }, []);

  const clearCache = useCallback(() => {
    if (aiInsightsService.clearCache) {
        aiInsightsService.clearCache();
    }
    log('info', 'Underlying service cache cleared');
  }, [log]);

  // ────────────────────────────────────────────────────────────────────────────
  // LIFECYCLE MANAGEMENT
  // ────────────────────────────────────────────────────────────────────────────
  
  // 1. Initial generation trigger
  useEffect(() => {
    generateInsights();
  }, [generateInsights]);

  // 2. Auto-refresh Background Cron
  useEffect(() => {
    if (autoRefresh && enabled) {
      refreshTimerRef.current = setInterval(() => {
        log('debug', 'Auto-refresh cron triggered (O(1) Poll)');
        generateInsights(false);
      }, refreshInterval);

      return () => {
        if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      };
    }
  }, [autoRefresh, enabled, refreshInterval, generateInsights, log]);

  // 3. Debug mode sync with underlying service
  useEffect(() => {
    if (aiInsightsService.setDebugMode) {
        aiInsightsService.setDebugMode(debugMode);
    }
  }, [debugMode]);

  // 4. Strict Cleanup on Unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      // Optional: flush debug logs to backend on unmount if required by enterprise policy
    };
  }, []);

  // ────────────────────────────────────────────────────────────────────────────
  // EXPORTED INTERFACE
  // ────────────────────────────────────────────────────────────────────────────
  return {
    // ── Data State ──
    insights,
    isGenerating,
    error,
    lastGenerated,
    generationCount,
    wsStatus,
    
    // ── Computed Metrics ──
    insightCount: insights.length,
    hasInsights: insights.length > 0,
    hasError: !!error,
    hasCritical: insights.some(i => i.severity === INSIGHT_SEVERITY.CRITICAL || i.type === INSIGHT_SEVERITY.CRITICAL),
    hasWarnings: insights.some(i => i.severity === INSIGHT_SEVERITY.WARNING || i.type === INSIGHT_SEVERITY.WARNING),
    
    // ── Interaction Actions ──
    refresh,
    clear,
    rateInsight,
    snoozeInsight,
    
    // ── Filtering & Searching ──
    getInsightByType,
    getInsightsBySeverity,
    filterInsights,
    // --- UPDATE: asset-linked insights
    insightsByAsset,
    getInsightsForAsset,
    assetInsightCount: insightsByAsset.size,
    // --- END UPDATE
    
    // ── Export & Diagnostics ──
    exportAsJSON,
    exportAsCSV,
    downloadDebugLogs,
    getCacheStats,
    clearCache,
    
    // ── Hook Configuration State ──
    isEnabled: enabled,
    isAutoRefresh: autoRefresh,
  };
};

export default useAIInsights;